import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { Entities } from '@gin-betting/core';

import { useUnmount } from 'hooks';

type ReserveCache<K extends Entities.EntityNames> = {
    [entity in K]: string[];
};

type ListId = string[];

interface IGCReserve<T extends Entities.EntityNames> {
    add(entityName: T, items: string[]): void;
    remove(entityName: T, items: string[]): void;
    replace(entityName: T, items: string[], prevItems: string[]): void;
    get(entityName: T): string[];
    useState<S extends string[]>(
        entityName: T,
        initialState?: S
    ): [S, Dispatch<SetStateAction<S | undefined>>];
}

const DEFAULT_IDS_STATE: ListId = [];

class GCReserve<T extends Entities.EntityNames> implements IGCReserve<T> {
    private cache: ReserveCache<T> = {} as ReserveCache<T>;

    constructor(entityNames: T[]) {
        entityNames.forEach(entityName => {
            this.cache[entityName] = [];
        });

        this.useState = this.useState.bind(this);
    }

    add(entityName: T, items: string[]): void {
        this.update(entityName, cached => cached.concat(items));
    }

    remove(entityName: T, items: string[]): void {
        this.update(entityName, cached => removeFirst(cached, items));
    }

    replace(entityName: T, items: string[], prevItems: string[]): void {
        this.update(entityName, cached =>
            removeFirst(cached, prevItems).concat(items)
        );
    }

    get(entityName: T): string[] {
        return this.cache[entityName].slice();
    }

    /** Enhances "useState" hook with automatic GC reserve updates.
     *
     *
     * - State will NOT be reset to the initial value on "entityName" change
     */
    useState(
        entityName: T,
        initialState: ListId = DEFAULT_IDS_STATE
    ): [ListId, Dispatch<SetStateAction<ListId | undefined>>] {
        /* Silence linter warning about inappropriate hook usage:
           "cannot be called in a class component"
        */
        /* eslint-disable react-hooks/rules-of-hooks */
        const entity = useRef(entityName);
        const [state, setHookState] = useState<ListId>(initialState);

        if (initialState && initialState.length > 0) {
            this.add(entity.current, initialState);
        }

        const setState: Dispatch<SetStateAction<ListId>> = useCallback(
            stateOrUpdater => {
                setHookState(prevState => {
                    const result =
                        typeof stateOrUpdater === 'function'
                            ? stateOrUpdater(prevState)
                            : stateOrUpdater;

                    if (result) {
                        const method = prevState.length > 0 ? 'replace' : 'add';

                        this[method](entity.current, result, prevState);
                    }

                    return result;
                });
            },
            []
        );

        useUnmount(() => {
            this.remove(entity.current, state);
        });

        return [state, setState];
        /* eslint-enable */
    }

    private update(
        entityName: T,
        updater: (cached: string[]) => string[]
    ): void {
        if (!this.cache[entityName]) return;

        const cached = this.cache[entityName];
        const next = updater(cached);

        this.cache[entityName] = next;
    }
}

function removeFirst<T>(source: T[], items: T[]): T[] {
    const removableItems = new Set(items);

    return source.filter(value => {
        const isRemovable = removableItems.has(value);

        if (isRemovable) {
            removableItems.delete(value);
        }

        return !isRemovable;
    });
}

export default new GCReserve(['matches']);
